/**
 * Spread prices, welcome credits and card ids shared by the app, its
 * functions and the landing. Prices here are the single source of truth.
 */

export const WELCOME_CREDITS = 70;

export const SPREAD_SLUGS = [
  "tak-chy-ni",
  "odna-karta",
  "try-karty",
  "khrest",
  "karyera",
  "piramida",
  "dolya",
  "shlyakh",
  "vidnosyny",
  "samopiznannya",
  "pidkova",
  "keltske-viyalo",
  "zdorovya",
  "keltskyi-khrest",
  "richnyi-rozklad",
] as const;

export type SharedSpreadSlug = (typeof SPREAD_SLUGS)[number];

type SharedSpreadInfo = {
  /** Key of `SPREAD_METADATA` in the app. */
  appName: string;
  cardCount: number;
  coinsPrice: number;
};

export const SHARED_SPREADS: Record<SharedSpreadSlug, SharedSpreadInfo> = {
  "tak-chy-ni": { appName: "Да/Нет", cardCount: 1, coinsPrice: 20 },
  "odna-karta": { appName: "Одна_карта", cardCount: 1, coinsPrice: 20 },
  "try-karty": { appName: "Три_карты", cardCount: 3, coinsPrice: 50 },
  khrest: { appName: "Крест", cardCount: 4, coinsPrice: 65 },
  karyera: { appName: "Карьера", cardCount: 5, coinsPrice: 80 },
  piramida: { appName: "Пирамида", cardCount: 5, coinsPrice: 80 },
  dolya: { appName: "Судьба", cardCount: 5, coinsPrice: 80 },
  shlyakh: { appName: "Путь", cardCount: 6, coinsPrice: 80 },
  vidnosyny: { appName: "Отношения", cardCount: 7, coinsPrice: 110 },
  samopiznannya: { appName: "Самопознание", cardCount: 7, coinsPrice: 110 },
  pidkova: { appName: "Подкова", cardCount: 7, coinsPrice: 110 },
  "keltske-viyalo": { appName: "Кельтский_веер", cardCount: 7, coinsPrice: 110 },
  zdorovya: { appName: "Здоровье", cardCount: 7, coinsPrice: 110 },
  "keltskyi-khrest": { appName: "Кельтский_крест", cardCount: 10, coinsPrice: 150 },
  "richnyi-rozklad": { appName: "Годовой_расклад", cardCount: 12, coinsPrice: 190 },
};

export function isSharedSpreadSlug(value: unknown): value is SharedSpreadSlug {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(SHARED_SPREADS, value)
  );
}

/** True when a new user's welcome credits alone cover this spread. */
export function isCoveredByWelcomeCredits(slug: SharedSpreadSlug): boolean {
  return SHARED_SPREADS[slug].coinsPrice <= WELCOME_CREDITS;
}

const SUIT_LETTER = {
  wands: "w",
  cups: "c",
  swords: "s",
  pentacles: "p",
} as const;

const COURT_NUMBER = {
  page: 11,
  knight: 12,
  queen: 13,
  king: 14,
} as const;

const LANDING_CARD_ID = /^(major|wands|cups|swords|pentacles)_([a-z0-9]+)$/;

/**
 * Landing card id (`major_0`, `cups_10`, `wands_queen`) to the app deck's
 * image path (`/cards/m00.jpg`, `/cards/c10.jpg`, `/cards/w13.jpg`).
 */
export function landingCardIdToAppImgSrc(id: string): string | null {
  const match = LANDING_CARD_ID.exec(id);
  if (!match) {
    return null;
  }

  const [, group, rank] = match;
  if (group === "major") {
    const n = /^\d+$/.test(rank) ? Number(rank) : NaN;
    return n >= 0 && n <= 21 ? `/cards/m${String(n).padStart(2, "0")}.jpg` : null;
  }

  const n =
    rank in COURT_NUMBER
      ? COURT_NUMBER[rank as keyof typeof COURT_NUMBER]
      : /^\d+$/.test(rank)
        ? Number(rank)
        : NaN;
  if (!(n >= 1 && n <= 14)) {
    return null;
  }

  const letter = SUIT_LETTER[group as keyof typeof SUIT_LETTER];
  return `/cards/${letter}${String(n).padStart(2, "0")}.jpg`;
}
